// Source: https://docs.google.com/document/d/1IxO36l_f6CJGLjfmNWTvziNJv7trLSTVOLaI9NoIkj0/edit

import capitalizedInLease from '../../../incentives/capitalization/capitalizedInLease';
import nonCapitalizedInLease from '../../../incentives/capitalization/nonCapitalizedInLease';
import capitalizedInPurchase from '../../../incentives/capitalization/capitalizedInPurchase';

import ResidualValue from '../../../financial/calcResidualValue';
import LoanPayment from '../../../financial/calcLoanPayment';

const VehicleCost = {
  msrp: function (vehicle) {
    if (!vehicle) return 0;

    return vehicle.msrp && parseInt(vehicle.msrp, 10) > 0
      ? parseInt(vehicle.msrp, 10)
      : 0;
  },

  msrpPlusTax: function (vehicle, salesTax, purchaseMethod) {
    const incentives = this.incentives(vehicle, purchaseMethod) || 0;
    const taxableAmount = this.msrp(vehicle) - incentives;
    const tax = taxableAmount * (salesTax / 100);
    return taxableAmount + tax;
  },

  afterIncentives: function (vehicle) {
    const incentives = capitalizedInPurchase(vehicle);
    return this.msrp(vehicle) - incentives;
  },

  downPayment: function (vehicle, salesTax, downPayment, purchaseMethod) {
    return (
      this.msrpPlusTax(vehicle, salesTax, purchaseMethod) * (downPayment / 100)
    );
  },

  resaleValue: function (purchaseMethod, vehicle, lifetimeMiles) {
    return this.msrp(vehicle) * ResidualValue(purchaseMethod, lifetimeMiles);
  },

  incentives: function (vehicle, purchaseMethod) {
    return purchaseMethod === 'lease'
      ? capitalizedInLease(vehicle)
      : capitalizedInPurchase(vehicle);
  },

  nonCapitalizedLeaseIncentives: function (vehicle) {
    return vehicle && vehicle.incentives ? nonCapitalizedInLease(vehicle) : 0;
  },

  loanAmountToBeFinanced: function (
    vehicle,
    salesTax,
    downPayment,
    purchaseMethod
  ) {
    return (
      this.msrpPlusTax(vehicle, salesTax, purchaseMethod) -
      this.downPayment(vehicle, salesTax, downPayment, purchaseMethod)
    );
  },

  monthlyLoanPayment: function (
    vehicle,
    monthsOfOwnership,
    interestRateAsBasisPoints,
    salesTax,
    downPayment,
    purchaseMethod
  ) {
    return LoanPayment.monthly(
      interestRateAsBasisPoints,
      monthsOfOwnership,
      this.loanAmountToBeFinanced(
        vehicle,
        salesTax,
        downPayment,
        purchaseMethod
      )
    );
  },

  monthlyLoanPaymentsTotal: function (
    vehicle,
    monthsOfOwnership,
    interestRateAsBasisPoints,
    salesTax,
    downPayment,
    purchaseMethod
  ) {
    return (
      monthsOfOwnership *
      this.monthlyLoanPayment(
        vehicle,
        monthsOfOwnership,
        interestRateAsBasisPoints,
        salesTax,
        downPayment,
        purchaseMethod
      )
    );
  },

  moneyFactor: function (interestRateAsBasisPoints) {
    return interestRateAsBasisPoints / 240000;
  },

  leaseCapitalizedCost: function (
    vehicle,
    salesTax,
    downPayment,
    purchaseMethod
  ) {
    return (
      this.msrp(vehicle) -
      this.incentives(vehicle, 'lease') -
      this.downPayment(vehicle, salesTax, downPayment, purchaseMethod)
    );
  },

  monthlyDepreciationCost: function (
    vehicle,
    lifetimeMiles,
    monthsOfOwnership,
    salesTax,
    downPayment,
    purchaseMethod
  ) {
    return (
      (this.leaseCapitalizedCost(
        vehicle,
        salesTax,
        downPayment,
        purchaseMethod
      ) -
        this.resaleValue('lease', vehicle, lifetimeMiles)) /
      monthsOfOwnership
    );
  },

  monthlyFinancingCost: function (
    vehicle,
    lifetimeMiles,
    interestRateAsBasisPoints,
    salesTax,
    downPayment,
    purchaseMethod
  ) {
    return (
      (this.leaseCapitalizedCost(
        vehicle,
        salesTax,
        downPayment,
        purchaseMethod
      ) +
        this.resaleValue('lease', vehicle, lifetimeMiles)) *
      this.moneyFactor(interestRateAsBasisPoints)
    );
  },

  monthlyLeaseDepreciationAndFinancingCosts: function (
    vehicle,
    annualMiles,
    monthsOfOwnership,
    interestRateAsBasisPoints,
    salesTax,
    downPayment,
    purchaseMethod
  ) {
    const lifetimeMiles = (annualMiles * monthsOfOwnership) / 12;
    return (
      this.monthlyDepreciationCost(
        vehicle,
        lifetimeMiles,
        monthsOfOwnership,
        salesTax,
        downPayment,
        purchaseMethod
      ) +
      this.monthlyFinancingCost(
        vehicle,
        lifetimeMiles,
        interestRateAsBasisPoints,
        salesTax,
        downPayment,
        purchaseMethod
      )
    );
  },

  monthlyLeasePayment: function (
    vehicle,
    annualMiles,
    monthsOfOwnership,
    interestRateAsBasisPoints,
    salesTax,
    downPayment,
    purchaseMethod
  ) {
    return (
      (1 + salesTax / 100) *
      this.monthlyLeaseDepreciationAndFinancingCosts(
        vehicle,
        annualMiles,
        monthsOfOwnership,
        interestRateAsBasisPoints,
        salesTax,
        downPayment,
        purchaseMethod
      )
    );
  },

  firstLeasePayment: function (
    vehicle,
    annualMiles,
    monthsOfOwnership,
    interestRateAsBasisPoints,
    salesTax,
    downPayment,
    purchaseMethod
  ) {
    return (
      this.downPayment(vehicle, salesTax, downPayment, purchaseMethod) +
      this.monthlyLeasePayment(
        vehicle,
        annualMiles,
        monthsOfOwnership,
        interestRateAsBasisPoints,
        salesTax,
        downPayment,
        purchaseMethod
      )
    );
  },

  total: function (
    vehicle,
    annualMiles,
    monthsOfOwnership,
    interestRateAsBasisPoints,
    purchaseMethod,
    includeResaleValue,
    salesTax,
    downPayment
  ) {
    if (!vehicle) return 0;

    const lifetimeMiles = (annualMiles * monthsOfOwnership) / 12;

    let cost = 0;

    const resaleValue = includeResaleValue
      ? this.resaleValue(purchaseMethod, vehicle, lifetimeMiles)
      : 0;

    switch (purchaseMethod) {
      case 'loan':
        cost =
          this.downPayment(vehicle, salesTax, downPayment, purchaseMethod) +
          this.monthlyLoanPaymentsTotal(
            vehicle,
            monthsOfOwnership,
            interestRateAsBasisPoints,
            salesTax,
            downPayment,
            purchaseMethod
          ) -
          this.incentives(vehicle, 'loan') -
          resaleValue;
        break;
      case 'lease':
        cost =
          this.firstLeasePayment(
            vehicle,
            annualMiles,
            monthsOfOwnership,
            interestRateAsBasisPoints,
            salesTax,
            downPayment,
            'lease'
          ) +
          (monthsOfOwnership - 1) *
            this.monthlyLeasePayment(
              vehicle,
              annualMiles,
              monthsOfOwnership,
              interestRateAsBasisPoints,
              salesTax,
              downPayment,
              'lease'
            ) -
          this.nonCapitalizedLeaseIncentives(vehicle);
        break;
      case 'cash':
        cost =
          this.msrpPlusTax(vehicle, salesTax, purchaseMethod) -
          this.incentives(vehicle, 'cash') -
          resaleValue;
        break;
      default:
        break;
    }
    return isFinite(cost) ? cost : 0;
  },

  perMile: function (
    vehicle,
    annualMiles,
    monthsOfOwnership,
    interestRateAsBasisPoints,
    purchaseMethod,
    includeResaleValue,
    salesTax,
    downPayment
  ) {
    if (!vehicle) return 0;

    const totalVehicleCost = this.total(
      vehicle,
      annualMiles,
      monthsOfOwnership,
      interestRateAsBasisPoints,
      purchaseMethod,
      includeResaleValue,
      salesTax,
      downPayment
    );
    const cost = totalVehicleCost / (annualMiles / 12) / monthsOfOwnership;

    return isFinite(cost) ? cost : 0;
  },
};

export default VehicleCost;
